// Cave84 — Bottles hooks (Phase 1 Week 4)
// TanStack Query wrappers around services/bottles.ts.
// Critical rule: bottles are NEVER deleted — only status is changed.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { Bottle, BottleStatus } from '@cave84/shared-types';
import {
  changeBottleStatus,
  createBottle,
  createBottles,
  getBottle,
  listBottlesByCellar,
  listBottlesByVintage,
  listBottlesByVintageIds,
  setBottlesSpecialOccasion,
  transferBottle,
  transferBottles,
  updateBottle,
  type BottleCreateInput,
  type BottleUpdateInput
} from '@/services/bottles';
import {
  completeOpenBottle, getConsumptionForBottle, upsertTastingNote,
  type ConsumptionRecord, type OpenBottleInput, type UpsertTastingNoteInput
} from '@/services/consumption';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') {
    throw new Error('Cave84: this mutation requires the owner role.');
  }
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useBottlesByVintage(vintageId: string | undefined): UseQueryResult<Bottle[]> {
  return useQuery({
    queryKey: vintageId ? qk.bottles.byWine(vintageId) : qk.bottles.byWine('__noop__'),
    queryFn: () => (vintageId ? listBottlesByVintage(vintageId) : Promise.resolve([])),
    enabled: !!vintageId
  });
}

/** Every bottle of a wine, across all of its vintages. */
export function useBottlesByVintageIds(vintageIds: string[]): UseQueryResult<Bottle[]> {
  return useQuery({
    queryKey: qk.bottles.byVintageIds(vintageIds),
    queryFn: () => listBottlesByVintageIds(vintageIds),
    enabled: vintageIds.length > 0
  });
}

export function useBottlesByCellar(
  cellarId: string | undefined,
  status: BottleStatus = 'in_cellar'
): UseQueryResult<Bottle[]> {
  return useQuery({
    queryKey: cellarId
      ? [...qk.bottles.byCellar(cellarId), status]
      : [...qk.bottles.byCellar('__noop__'), status],
    queryFn: () =>
      cellarId ? listBottlesByCellar(cellarId, status) : Promise.resolve([]),
    enabled: !!cellarId
  });
}

export function useBottle(id: string | undefined): UseQueryResult<Bottle | null> {
  return useQuery({
    queryKey: id ? qk.bottles.byId(id) : qk.bottles.byId('__noop__'),
    queryFn: () => (id ? getBottle(id) : Promise.resolve(null)),
    enabled: !!id
  });
}

/**
 * Drinking location + tasting note captured by OpenBottleModal (Phase 7).
 * Only meaningful once a bottle's status is 'consumed' — callers should gate
 * `enabled` on that, since in_cellar bottles never have a ConsumptionEvent.
 */
export function useConsumptionForBottle(
  bottleId: string | undefined,
  enabled = true
): UseQueryResult<ConsumptionRecord> {
  return useQuery({
    queryKey: bottleId ? qk.consumption.byBottle(bottleId) : qk.consumption.byBottle('__noop__'),
    queryFn: () => (bottleId ? getConsumptionForBottle(bottleId) : Promise.resolve({ event: null, tastingNote: null })),
    enabled: !!bottleId && enabled
  });
}

/** Add or edit the tasting note / drinking location for an already-consumed bottle. */
export function useUpsertTastingNote(): UseMutationResult<void, Error, UpsertTastingNoteInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertTastingNoteInput) => {
      assertOwner();
      return upsertTastingNote(input);
    },
    onSuccess: (_data, { bottleId }) => {
      void qc.invalidateQueries({ queryKey: qk.consumption.byBottle(bottleId) });
    }
  });
}

// ---------------------------------------------------------------------------
// Create — single
// ---------------------------------------------------------------------------

export function useCreateBottle(): UseMutationResult<string, Error, BottleCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BottleCreateInput) => {
      assertOwner();
      return createBottle(input);
    },
    onSuccess: (_id, input) => {
      // Broad invalidation so the Inventory page (which uses ['bottles','all','in_cellar'])
      // and any vintage count query get refreshed.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.bottles.byCellar(input.current_cellar_id) });
    }
  });
}

// ---------------------------------------------------------------------------
// Create — bulk (qty bottles, same lot)
// ---------------------------------------------------------------------------

interface BulkCreateInput {
  input: BottleCreateInput;
  qty: number;
}

export function useCreateBottles(): UseMutationResult<string[], Error, BulkCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input, qty }: BulkCreateInput) => {
      assertOwner();
      return createBottles(input, qty);
    },
    onSuccess: (_ids, { input }) => {
      // Broad invalidation so the Inventory page (which uses ['bottles','all','in_cellar'])
      // and any vintage/wine count query get refreshed immediately.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.bottles.byCellar(input.current_cellar_id) });
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
    }
  });
}

// ---------------------------------------------------------------------------
// Open (consume)
// ---------------------------------------------------------------------------

interface OpenBottleMutationInput extends Omit<OpenBottleInput, 'bottleId' | 'vintageId'> {
  bottle: Bottle;
}

export function useOpenBottle(): UseMutationResult<void, Error, OpenBottleMutationInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bottle, ...rest }: OpenBottleMutationInput) => {
      assertOwner();
      return completeOpenBottle({ bottleId: bottle.id, vintageId: bottle.vintage_id, ...rest });
    },
    onSuccess: () => {
      // qk.bottles.all prefix-invalidates ['bottles','all','in_cellar'] (Inventory)
      // and ['bottles','all','consumed'] (Consumed archive) — byId/byCellar alone
      // do NOT match those keys, which left the Inventory table stale after opening.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    }
  });
}

// ---------------------------------------------------------------------------
// Change status (gifted / sold / lost / broken / trading)
// ---------------------------------------------------------------------------

interface ChangeStatusInput {
  bottle: Bottle;
  status: Exclude<BottleStatus, 'in_cellar' | 'consumed'>;
}

export function useChangeBottleStatus(): UseMutationResult<void, Error, ChangeStatusInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bottle, status }: ChangeStatusInput) => {
      assertOwner();
      return changeBottleStatus(bottle.id, status, bottle.vintage_id);
    },
    onSuccess: () => {
      // Prefix invalidation — covers byId, byCellar AND ['bottles','all',status]
      // (the Inventory join query), which the narrower keys missed.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
    }
  });
}

// ---------------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------------

interface TransferInput {
  bottle: Bottle;
  toCellarId: string;
  toLocationId?: string;
  reason?: string;
}

export function useTransferBottle(): UseMutationResult<void, Error, TransferInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bottle, toCellarId, toLocationId, reason }: TransferInput) => {
      assertOwner();
      return transferBottle(bottle, toCellarId, toLocationId, reason);
    },
    onSuccess: () => {
      // Prefix invalidation — also refreshes ['bottles','all','in_cellar'] so the
      // Inventory table reflects the new cellar immediately.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
    }
  });
}

// ---------------------------------------------------------------------------
// Bulk operations — driven by the Inventory table's row selection
// ---------------------------------------------------------------------------

interface BulkTransferInput {
  bottles: Bottle[];
  toCellarId: string;
  toLocationId?: string;
  reason?: string;
}

/** Move a selection of bottles into one cellar. Resolves to the count moved. */
export function useTransferBottles(): UseMutationResult<number, Error, BulkTransferInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bottles, toCellarId, toLocationId, reason }: BulkTransferInput) => {
      assertOwner();
      return transferBottles(bottles, toCellarId, toLocationId, reason);
    },
    onSuccess: () => {
      // Same prefix invalidation as the single-bottle transfer: ['bottles','all',…]
      // is what the Inventory join query reads, and cellar bottle counts move too.
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.cellars.all });
    }
  });
}

interface BulkSpecialOccasionInput {
  bottleIds: string[];
  isSpecial: boolean;
}

/** Flag or unflag a selection of bottles as special-occasion. */
export function useSetBottlesSpecialOccasion(): UseMutationResult<number, Error, BulkSpecialOccasionInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bottleIds, isSpecial }: BulkSpecialOccasionInput) => {
      assertOwner();
      return setBottlesSpecialOccasion(bottleIds, isSpecial);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
    }
  });
}

// ---------------------------------------------------------------------------
// Update details
// ---------------------------------------------------------------------------

interface UpdateBottleInput {
  id: string;
  cellarId: string; // needed for cache invalidation
  vintageId: string;
  patch: BottleUpdateInput;
}
export function useUpdateBottle(): UseMutationResult<void, Error, UpdateBottleInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: UpdateBottleInput) => {
      assertOwner();
      return updateBottle(id, patch);
    },
    onSuccess: (_data, { id, cellarId, vintageId }) => {
      void qc.invalidateQueries({ queryKey: qk.bottles.byId(id) });
      void qc.invalidateQueries({ queryKey: qk.bottles.byCellar(cellarId) });
      void qc.invalidateQueries({ queryKey: qk.vintages.byId(vintageId) });
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
    }
  });
}
